import { useAtomValue } from 'jotai';

import type {
  TBenchmarkingProviderInsights,
  TBenchmarkingResponse,
  TTreatmentProduction,
  TTreatmentType,
} from '@/react-query/insights/types';
import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';
import { usePreferences } from '@/react-query/usePreferences';

import {
  isCountsAtom,
  materialityFilterOpportunityCountsAtom,
  materialityFilterOpportunityDollarsAtom,
  materialityFilterProductionCountsAtom,
  materialityFilterProductionDollarsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  txTypeAtom,
} from '../../atoms';

// Filter for pilot region
export const useEnabledPilotRegionFilter = (
  clinicalData?: TBenchmarkingResponse,
  applyFilter?: boolean,
) => {
  const { data: prefs } = usePreferences();
  const pilotRegion = prefs?.enableInsightsPilotRegion;

  const practiceGroups = useGetPracticeGroups();
  const enabledPractices = practiceGroups?.data?.data
    ?.find((group) => group.id === pilotRegion)
    ?.practices.map((practice) => practice.practiceId);

  if (pilotRegion && applyFilter !== false && clinicalData) {
    return {
      ...clinicalData,
      practices: clinicalData?.practices?.filter((practice) =>
        enabledPractices?.includes(practice.practiceId),
      ),
    };
  }

  return clinicalData;
};

// Filter for practices and regions
export const usePracticesFilter = (
  clinicalData?: TBenchmarkingResponse,
  applyFilter?: boolean,
) => {
  const selectedPractices = useAtomValue(selectedPracticesAtom);

  if (selectedPractices?.length && applyFilter !== false && clinicalData) {
    return {
      ...clinicalData,
      practices: clinicalData?.practices?.filter(
        (practice) =>
          !selectedPractices?.length ||
          selectedPractices?.includes(practice.practiceVideaId),
      ),
    };
  }

  return clinicalData;
};

export const useTreatmentsFilter = (
  clinicalData?: TBenchmarkingResponse,
  applyFilter?: boolean,
) => {
  const selectedTreatments = useAtomValue(txTypeAtom);

  const { data: prefs } = usePreferences();
  const enabledTreatments = prefs?.insightsEnabledRecos;

  // List of treatments to show
  const treatments = enabledTreatments
    ?.filter(
      (treatment) =>
        !selectedTreatments.length || selectedTreatments?.includes(treatment),
    )
    ?.reduce((acc, treatment) => {
      if (!acc.includes(treatment)) {
        acc.push(treatment);
      }
      return acc;
    }, [] as TTreatmentType[]);

  if (treatments?.length && applyFilter !== false && clinicalData) {
    return {
      ...clinicalData,
      practices: clinicalData.practices?.map((practice) => ({
        ...practice,
        providers: practice.providers?.map((provider) => ({
          ...provider,

          // Filter treatments for recommendations
          recommendations: Object.fromEntries(
            Object.entries(provider.recommendations ?? {}).filter(
              ([treatmentType]) =>
                treatments.includes(treatmentType as TTreatmentType),
            ),
          ) as Partial<{
            [key in TTreatmentType]: { current: any; previous: any };
          }>,

          // Filter treatments for production
          production: Object.fromEntries(
            Object.entries(provider.production ?? {}).filter(
              ([treatmentType]) =>
                treatments.includes(treatmentType as TTreatmentType),
            ),
          ) as Partial<{ [key in TTreatmentType]: TTreatmentProduction }>,
        })) as TBenchmarkingProviderInsights[],
      })),
    };
  }

  return clinicalData;
};

export const useProviderRoleFilter = (
  clinicalData?: TBenchmarkingResponse,
  applyFilter?: boolean,
) => {
  const selectedProviderRole = useAtomValue(selectedProviderRoleAtom);

  if (selectedProviderRole?.length && applyFilter !== false && clinicalData) {
    return {
      ...clinicalData,
      practices: clinicalData.practices?.map((practice) => ({
        ...practice,
        providers: practice.providers?.filter(
          (provider) =>
            provider?.providerRole &&
            selectedProviderRole.includes(provider.providerRole),
        ),
      })),
    };
  }

  return clinicalData;
};

// Filter by materiality (minimum $/counts for a provider to be shown)
export const useMaterialityFilter = (
  clinicalData?: TBenchmarkingResponse,
  applyFilter?: boolean,
) => {
  const isCounts = useAtomValue(isCountsAtom);

  // Materiality filters
  const materialityFilterProductionDollars = useAtomValue(
    materialityFilterProductionDollarsAtom,
  );
  const materialityFilterProductionCounts = useAtomValue(
    materialityFilterProductionCountsAtom,
  );
  const materialityFilterOpportunityDollars = useAtomValue(
    materialityFilterOpportunityDollarsAtom,
  );
  const materialityFilterOpportunityCounts = useAtomValue(
    materialityFilterOpportunityCountsAtom,
  );

  // Selected treatments
  const selectedTreatments = useAtomValue(txTypeAtom);
  const { data: prefs } = usePreferences();
  const enabledTreatments = prefs?.insightsEnabledRecos;

  const treatments = enabledTreatments
    ?.filter(
      (treatment) =>
        !selectedTreatments.length || selectedTreatments?.includes(treatment),
    )
    ?.reduce((acc, treatment) => {
      if (!acc.includes(treatment)) {
        acc.push(treatment);
      }
      return acc;
    }, [] as TTreatmentType[]);

  if (treatments && applyFilter !== false && clinicalData) {
    return {
      ...clinicalData,
      practices: clinicalData.practices?.map((practice) => ({
        ...practice,
        providers: practice.providers?.filter((provider) => {
          // Get total production based on selected treatments
          const production = Object.entries(provider.production ?? {})
            .filter(([treatmentType]) =>
              treatments.includes(treatmentType as TTreatmentType),
            )
            .map(([, value]) =>
              isCounts
                ? (value.current?.count ?? 0)
                : (value.current?.value ?? 0),
            )
            .reduce((acc, value) => acc + value, 0);

          // Get total recommendations based on selected treatments
          const recommendations = Object.entries(provider.recommendations ?? {})
            .filter(([treatmentType]) =>
              treatments.includes(treatmentType as TTreatmentType),
            )
            .map(([treatmentType, value]) => {
              const treatmentCost = isCounts
                ? 1
                : (practice.estimatedTreatmentValues[
                    treatmentType as TTreatmentType
                  ]?.value ?? 1);

              const recommendationCount =
                (value.current?.planned?.treatmentCount ?? 0) +
                (value.current?.unplanned?.treatmentCount ?? 0);

              return recommendationCount * treatmentCost;
            })
            .reduce((acc, value) => acc + value, 0);

          if (isCounts) {
            return (
              production >= materialityFilterProductionCounts &&
              recommendations >= materialityFilterOpportunityCounts
            );
          }

          if (!isCounts) {
            return (
              production >= materialityFilterProductionDollars &&
              recommendations >= materialityFilterOpportunityDollars
            );
          }
        }),
      })),
    };
  }

  return clinicalData;
};
